import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { Box, Button, Flex, Notification, Small } from "orcs-design-system";

import { useDesignOptionId } from "./overlay/useDesignOptionId";
import { useOverlayOptionId } from "./overlay/useOverlayOptionId";
import { getDesignOptionLabel } from "./designOptionLabels";
import {
  getOptionPageSupport,
  type OptionPageSupport,
} from "./optionPageSupport";
import { OptionPageSupportContext } from "./OptionPageSupportContext";
import { useDesignWorkspaceOptions } from "./useDesignWorkspaceOptions";

const READ_ONLY_MESSAGE =
  "Showing baseline data, not the selected design option. Edits are disabled on this page. Switch to Baseline to edit.";

const useSelectedOptionName = (optionId: string): string | undefined => {
  const { options, design } = useDesignWorkspaceOptions();
  const option = options.find((item) => item.id === optionId);
  return option
    ? getDesignOptionLabel(option, design?.baselineDataDate)
    : undefined;
};

type BannerProps = {
  support: OptionPageSupport;
  optionName: string | undefined;
};

const OptionBanner = ({ support, optionName }: BannerProps) => {
  if (support === "blocked") {
    return null;
  }

  return (
    <Box mb="r" data-testid="option-page-banner" data-support={support}>
      <Notification
        variant={support === "supported" ? "informational" : "warning"}
        closable={false}
      >
        <Small>
          {support === "supported"
            ? `Editing design option: ${optionName ?? "selected option"}`
            : READ_ONLY_MESSAGE}
        </Small>
      </Notification>
    </Box>
  );
};

type BlockedNoticeProps = { onSwitchToBaseline: () => void };

const BlockedNotice = ({ onSwitchToBaseline }: BlockedNoticeProps) => (
  <Flex flexDirection="column" gap="r" alignItems="flex-start">
    <Notification variant="warning" closable={false}>
      <Small data-testid="option-page-blocked-notice">
        This page does not support design options yet. Switch to Baseline to use
        it.
      </Small>
    </Notification>
    <Button onClick={onSwitchToBaseline}>Switch to Baseline</Button>
  </Flex>
);

type OptionPageGuardProps = { children: ReactNode };

/**
 * Mounted once in the app layout. Labels every page with the selected design
 * option and keeps pages that cannot honour an option from writing the
 * baseline.
 */
const OptionPageGuard = ({ children }: OptionPageGuardProps) => {
  const { pathname } = useLocation();
  const optionId = useOverlayOptionId();
  const { setOptionId } = useDesignOptionId();

  if (!optionId) {
    return <>{children}</>;
  }

  const support = getOptionPageSupport(pathname);

  return (
    <OptionGuardBody
      optionId={optionId}
      support={support}
      onSwitchToBaseline={() => setOptionId(undefined)}
    >
      {children}
    </OptionGuardBody>
  );
};

type OptionGuardBodyProps = {
  optionId: string;
  support: OptionPageSupport;
  onSwitchToBaseline: () => void;
  children: ReactNode;
};

const OptionGuardBody = ({
  optionId,
  support,
  onSwitchToBaseline,
  children,
}: OptionGuardBodyProps) => {
  const optionName = useSelectedOptionName(optionId);

  return (
    <>
      <OptionBanner support={support} optionName={optionName} />
      {support === "blocked" ? (
        <BlockedNotice onSwitchToBaseline={onSwitchToBaseline} />
      ) : (
        <OptionPageSupportContext.Provider value={support}>
          {children}
        </OptionPageSupportContext.Provider>
      )}
    </>
  );
};

export default OptionPageGuard;
